import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import ChatServices from "../services/ChatServices";
import "../styles/quickChat.css";

/**
 * Send one support message to a player without leaving the request queue.
 *
 * Written for the case an admin actually hits: a pending deposit or withdrawal
 * is missing something (no UTR, wrong bank name), and the player needs asking
 * before it can be approved or rejected. It writes to the same conversation as
 * the Support Chat page, so the reply lands in the normal inbox.
 *
 * `player` needs { id, name, game_id, mobile } - the request rows already
 * carry all four under their own column names, so each queue maps them in.
 */
const QuickChatModal = ({ player, presets = [], onClose, onSent }) => {
  const history = useHistory();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = message.trim();
    if (!text) {
      toast.error("Type a message first");
      return;
    }

    setSending(true);
    try {
      const api = new ChatServices();
      const res = await api.sendMessage(player.id, text);
      if (res?.isSuccess) {
        toast.success(`Message sent to ${player.name || "the player"}`);
        setMessage("");
        onSent?.(res.data);
        onClose();
      } else {
        toast.error(res?.message || "Could not send message");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="md">
      <Modal.Header>
        <Modal.Title className="qc-title">Message player</Modal.Title>
        <button className="qc-close" onClick={onClose} aria-label="Close">
          <RiCloseLargeFill />
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="qc-player">
          <span className="qc-avatar" aria-hidden="true">
            {(player.name || "?").trim().charAt(0).toUpperCase()}
          </span>
          <div className="qc-player-who">
            <strong>{player.name || "Player"}</strong>
            <span>
              {player.game_id} · {player.mobile}
            </span>
          </div>
        </div>

        {presets.length > 0 && (
          <div className="qc-presets">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                className="qc-preset"
                onClick={() => setMessage(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <textarea
          className="qc-input"
          rows={4}
          maxLength={2000}
          autoFocus
          placeholder="Type your message…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl/Cmd+Enter sends; plain Enter makes a new line, because this
            // box is for a considered question rather than a rapid chat reply.
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send();
          }}
        />

        <div className="qc-foot">
          <button
            type="button"
            className="qc-link"
            onClick={() => history.push("/support-chat")}
          >
            Open full conversation
          </button>
          <span className="qc-count">{message.length}/2000</span>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button className="qc-btn qc-btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          className="qc-btn qc-btn-send"
          onClick={send}
          disabled={!message.trim() || sending}
        >
          {sending ? "Sending…" : "Send message"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickChatModal;
